import { PLACEHOLDER_VALUE } from '@/constants';
import { ROW_ERROR_FIELD } from '@/lib/map-field-errors';
import type { InstrumentRunDataRow } from '@/types';
import Table from '@nautilusbio/nb-design-system/Table';
import type { CellClickedEvent, CellValueChangedEvent, ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import tableStyles from '../../../../styles/components/table.module.css';

import { isLibraryGroupEditable } from './libraryGroupValidation';
import { createEmptyRows } from './reducer';
import { StudyNameCellRenderer } from './StudyNameCellRenderer';
import styles from './styles.module.css';
import type { CellErrors } from './types';

interface SampleTableProps {
  rowData: InstrumentRunDataRow[];
  allRowData?: InstrumentRunDataRow[];
  onRowDataChange?: (data: InstrumentRunDataRow[]) => void;
  updateSampleAt?: (rowIndex: number, patch: Partial<InstrumentRunDataRow>) => void;
  onBulkApply?: (rows: InstrumentRunDataRow[]) => void;
  /** When true, highlights cells with missing required values in danger red. */
  showCellErrors?: boolean;
  /** Per-row, per-field error messages from the backend. Key `__row__` means a whole-row error. */
  cellErrors?: CellErrors;
  readOnlyColumns?: string[];
  showCopyToRows?: boolean;
  /** Optional ref lifted from a parent — if provided, the parent controls scrolling/refresh via the grid API. */
  gridRef?: React.RefObject<AgGridReact<InstrumentRunDataRow> | null>;
  /** The library groups active for the current libraryGroupCount selection. Empty means the run
   *  config hasn't loaded yet — see SampleManager's no-run-config fallback. */
  activeLibraryGroups?: string[];
}

const SampleTable: FC<SampleTableProps> = ({
  rowData,
  allRowData,
  onRowDataChange,
  updateSampleAt,
  onBulkApply,
  showCellErrors = false,
  cellErrors,
  readOnlyColumns = [],
  showCopyToRows = false,
  gridRef,
  activeLibraryGroups = [],
}) => {
  const { t } = useTranslation(['create', 'general', 'sampleManager']);
  const ownGridRef = useRef<AgGridReact<InstrumentRunDataRow>>(null);
  const effectiveRef = gridRef ?? ownGridRef;

  // Stable ref so cellClassRules closures always read the latest errors without
  // needing to be recreated — AG Grid doesn't reliably hot-swap callback fns in
  // existing ColDefs when columnDefs prop changes.
  const cellErrorsRef = useRef<CellErrors>(cellErrors ?? {});
  useEffect(() => {
    cellErrorsRef.current = cellErrors ?? {};
    effectiveRef.current?.api?.refreshCells({ force: true });
  }, [cellErrors, effectiveRef]);

  // Same rationale as cellErrorsRef above: AG Grid doesn't reliably hot-swap editable/cellEditor
  // callbacks in existing ColDefs when the columnDefs prop changes, so the closures below read
  // this ref rather than closing over `activeLibraryGroups` directly.
  const activeLibraryGroupsRef = useRef<string[]>(activeLibraryGroups);
  useEffect(() => {
    activeLibraryGroupsRef.current = activeLibraryGroups;
    effectiveRef.current?.api?.refreshCells({ force: true });
  }, [activeLibraryGroups, effectiveRef]);

  // Local state for row data to enable inline editing
  const [localRowData, setLocalRowData] = useState<InstrumentRunDataRow[]>(() =>
    rowData.length === 0 ? createEmptyRows() : rowData,
  );

  // Determine if pagination is needed (more than 12 rows)
  const usePagination = localRowData.length > 12;

  // Sync localRowData whenever the rowData prop genuinely changes (e.g. resize,
  // CSV upload, draft load). Cell edits never change the rowData prop because
  // SampleManager.state.rowData is only updated via dispatch — so there is no
  // risk of accidentally overwriting in-progress edits here.
  useEffect(() => {
    if (rowData.length === 0) {
      setLocalRowData(createEmptyRows());
      return;
    }
    setLocalRowData(rowData);
  }, [rowData]);

  // Handle cell value changes
  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<InstrumentRunDataRow>) => {
      const updatedData = [...localRowData];
      const rowIndex = event.rowIndex;

      if (rowIndex !== undefined && rowIndex !== null && rowIndex >= 0 && event.data) {
        // Create a new object to avoid shared references
        updatedData[rowIndex] = { ...event.data };
        setLocalRowData(updatedData);

        // Notify parent component of changes
        if (onRowDataChange) {
          // Filter out completely empty rows before notifying parent
          // A row is non-empty if it has any field filled (demux_index can be 0, which is valid)
          const nonEmptyRows = updatedData.filter(row => {
            // A row is non-empty if any primary field is filled OR the control is set
            const hasPrimary = row.name || row.study_name || row.org_name || row.well_position;
            const hasControl = row.is_control === true || (row.sample_type && row.sample_type !== '');

            return Boolean(hasPrimary) || Boolean(hasControl);
          });

          onRowDataChange(nonEmptyRows);
        }
      }
    },
    [localRowData, onRowDataChange],
  );

  // Column Definitions with editable configuration
  const colDefs = useMemo<ColDef<InstrumentRunDataRow>[]>(() => {
    const isReadOnly = (field: string) => readOnlyColumns.includes(field);
    const readOnlyClass = (field: string) => (isReadOnly(field) ? styles.readOnlyCell : undefined);

    return [
      {
        field: 'name',
        headerName: t('general:name'),
        editable: () => !isReadOnly('name'),
        cellClass: readOnlyClass('name'),
        cellEditor: 'agTextCellEditor',
        cellEditorParams: { maxLength: 100 },
        cellClassRules: {
          [styles.cellError]: params => {
            const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
            return (
              (showCellErrors &&
                (!params.value || String(params.value).trim() === '' || params.value === PLACEHOLDER_VALUE)) ||
              Boolean(rowErrs?.['name']) ||
              Boolean(rowErrs?.[ROW_ERROR_FIELD])
            );
          },
        },
        tooltipValueGetter: params => {
          const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
          return rowErrs?.['name'] ?? rowErrs?.[ROW_ERROR_FIELD] ?? undefined;
        },
      },
      {
        field: 'org_name',
        headerName: t('create:run.orgName'),
        editable: false,
        cellClass: clsx(readOnlyClass('org_name'), styles.readOnlyCell),
        cellClassRules: {
          [styles.cellError]: params => {
            const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
            return (
              (showCellErrors && (!params.value || params.value === PLACEHOLDER_VALUE)) ||
              Boolean(rowErrs?.['org_name']) ||
              Boolean(rowErrs?.[ROW_ERROR_FIELD])
            );
          },
        },
        tooltipValueGetter: params => {
          const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
          return rowErrs?.['org_name'] ?? rowErrs?.[ROW_ERROR_FIELD] ?? undefined;
        },
      },
      {
        field: 'study_name',
        headerName: t('create:run.studyName'),
        cellClass: readOnlyClass('study_name'),
        editable: false,
        cellRenderer: (params: ICellRendererParams<InstrumentRunDataRow>) => (
          <StudyNameCellRenderer
            {...params}
            allRowData={allRowData}
            onBulkApply={onBulkApply}
            showCopyToRows={showCopyToRows && !isReadOnly('study_name')}
            updateSampleAt={updateSampleAt}
          />
        ),
        cellClassRules: {
          [styles.cellError]: params => {
            const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
            return (
              (showCellErrors && (!params.value || params.value === PLACEHOLDER_VALUE)) ||
              Boolean(rowErrs?.['study_name']) ||
              Boolean(rowErrs?.[ROW_ERROR_FIELD])
            );
          },
        },
        tooltipValueGetter: params => {
          const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
          return rowErrs?.['study_name'] ?? rowErrs?.[ROW_ERROR_FIELD] ?? undefined;
        },
      },
      {
        cellClass: clsx(tableStyles.tableCellCenter, readOnlyClass('well_position')),
        field: 'well_position',
        headerName: t('create:run.wellPosition'),
        editable: () => !isReadOnly('well_position'),
        cellEditor: 'agTextCellEditor',
        cellEditorParams: { maxLength: 2 },
        cellClassRules: {
          [styles.cellError]: params => {
            const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
            return (
              (showCellErrors && (!params.value || params.value === PLACEHOLDER_VALUE)) ||
              Boolean(rowErrs?.['well_position']) ||
              Boolean(rowErrs?.[ROW_ERROR_FIELD])
            );
          },
        },
        tooltipValueGetter: params => {
          const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
          return rowErrs?.['well_position'] ?? rowErrs?.[ROW_ERROR_FIELD] ?? undefined;
        },
        width: 80,
      },
      {
        cellClass: clsx(tableStyles.tableCellCenter, readOnlyClass('demux_index')),
        field: 'demux_index',
        headerName: t('create:run.demuxIndex'),
        editable: () => !isReadOnly('demux_index'),
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: { min: 0, precision: 0 },
        valueParser: params => {
          const value = params.newValue;
          return value === '' || value === null ? 0 : Number(value);
        },
        cellClassRules: {
          [styles.cellError]: params => {
            const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
            return Boolean(rowErrs?.['demux_index']) || Boolean(rowErrs?.[ROW_ERROR_FIELD]);
          },
        },
        tooltipValueGetter: params => {
          const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
          return rowErrs?.['demux_index'] ?? rowErrs?.[ROW_ERROR_FIELD] ?? undefined;
        },
        width: 80,
      },
      {
        cellClass: clsx(tableStyles.tableCellCenter, readOnlyClass('flow_cell')),
        field: 'flow_cell',
        headerName: t('sampleManager:flow_cell'),
        editable: false,
        width: 90,
      },
      {
        cellClass: clsx(tableStyles.tableCellCenter, readOnlyClass('lane')),
        field: 'lane',
        headerName: t('sampleManager:lane'),
        editable: false,
        width: 70,
      },
      {
        field: 'library_group',
        headerName: t('sampleManager:library_group'),
        cellClass: readOnlyClass('library_group'),
        editable: () => isLibraryGroupEditable(activeLibraryGroupsRef.current) && !isReadOnly('library_group'),
        // cellEditorSelector, not a function-valued cellEditor: AG Grid v36's documented API for
        // choosing an editor dynamically (colDef.cellEditor/cellEditorParams are static or string
        // values — a function there is not a supported per-edit-start hook).
        cellEditorSelector: () =>
          isLibraryGroupEditable(activeLibraryGroupsRef.current)
            ? { component: 'agSelectCellEditor', params: { values: activeLibraryGroupsRef.current } }
            : { component: 'agTextCellEditor' },
        cellClassRules: {
          [styles.cellError]: params => {
            const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
            return Boolean(rowErrs?.['library_group']) || Boolean(rowErrs?.[ROW_ERROR_FIELD]);
          },
        },
        tooltipValueGetter: params => {
          const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
          return rowErrs?.['library_group'] ?? rowErrs?.[ROW_ERROR_FIELD] ?? undefined;
        },
      },
      {
        field: 'sample_group',
        headerName: t('sampleManager:sample_group'),
        cellClass: readOnlyClass('sample_group'),
        editable: () => !isReadOnly('sample_group'),
        cellEditor: 'agTextCellEditor',
        cellClassRules: {
          [styles.cellError]: params => {
            const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
            return Boolean(rowErrs?.['sample_group']) || Boolean(rowErrs?.[ROW_ERROR_FIELD]);
          },
        },
        tooltipValueGetter: params => {
          const rowErrs = cellErrorsRef.current[params.node?.rowIndex ?? -1];
          return rowErrs?.['sample_group'] ?? rowErrs?.[ROW_ERROR_FIELD] ?? undefined;
        },
      },
    ];
  }, [t, updateSampleAt, onBulkApply, allRowData, showCellErrors, readOnlyColumns, showCopyToRows]);

  // Default column definition for all columns
  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    [],
  );

  // Single-click starts editing for all editable cells
  const onCellClicked = useCallback((event: CellClickedEvent<InstrumentRunDataRow>) => {
    if (event.rowIndex !== null && event.rowIndex !== undefined && event.column) {
      const colId = event.column.getColId();
      event.api.startEditingCell({
        rowIndex: event.rowIndex,
        colKey: colId,
      });
    }
  }, []);

  return (
    <Table
      className={styles.sampleTable}
      columnDefs={colDefs}
      defaultColDef={defaultColDef}
      domLayout="autoHeight"
      enableAdvancedFilter={false}
      pagination={usePagination}
      paginationPageSize={12}
      paginationPageSizeSelector={false}
      rowData={localRowData}
      suppressContextMenu={true}
      onCellValueChanged={onCellValueChanged}
      onCellClicked={onCellClicked}
      ref={effectiveRef}
      stopEditingWhenCellsLoseFocus={true}
      container={{ 'data-testid': 'sample-table' }}
      tableId="sampleTable"
      enableBrowserTooltips={true}
      enableResponsiveResize={false}
      suppressClickEdit={true}
      undoRedoCellEditing={true}
      undoRedoCellEditingLimit={20}
      autoSizeStrategy={{ type: 'fitCellContents' }}
    />
  );
};

export default SampleTable;
